import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../pages/api';

const BasketContext = createContext();

export const useBasket = () => useContext(BasketContext);

export const BasketProvider = ({ children }) => {
    const [basketCount, setBasketCount] = useState(() => {
        const savedCount = localStorage.getItem('basketCount');
        return savedCount ? parseInt(savedCount, 10) : 0;
    });
    const [isUpdating, setIsUpdating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sessionReady, setSessionReady] = useState(false);

    const fetchBasketCount = async () => {
        if (!sessionReady) {
            console.log('Session not ready, skipping basket count fetch');
            return;
        }
        try {
            const response = await api.get('/api/basket/count/');
            if (response?.data?.count !== undefined) {
                setBasketCount(response.data.count);
                localStorage.setItem('basketCount', response.data.count.toString());
            }
        } catch (error) {
            if (!error.message.includes('canceled') && !error.message.includes('No valid session')) {
                console.error('Error fetching basket count:', error);
            }
        }
    };

    const updateBasketCount = (newCount) => {
        setBasketCount(newCount);
        localStorage.setItem('basketCount', newCount.toString());
    };

    const initBasket = async () => {
        if (!sessionReady) {
            console.log('Session not ready, skipping basket initialization');
            return;
        }
        try {
            const response = await api.get('/api/basket/');
            const basketItems = Array.isArray(response.data) ? response.data : response.data?.items;
            if (Array.isArray(basketItems)) {
                const totalCount = basketItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
                setBasketCount(totalCount);
                localStorage.setItem('basketCount', totalCount.toString());
            }
        } catch (error) {
            if (!error.message.includes('No valid session')) {
                console.error('Error initializing basket:', error);
            }
        }
    };

    // Wait for session to be ready
    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            setSessionReady(true);
            setLoading(false);
        }
        return () => {
            isMounted = false;
        };
    }, []);

    // Initialize basket once session is ready
    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        const init = async () => {
            if (sessionReady) {
                try {
                    await initBasket();
                    if (isMounted) {
                        await fetchBasketCount();
                    }
                } catch (error) {
                    if (!error.message.includes('No valid session')) {
                        console.error('Error initializing basket:', error);
                    }
                }
            }
        };

        init();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [sessionReady]);

    const addToCart = async (product, quantity, sku, metadata = {}) => {
        if (!sessionReady) {
            throw new Error('Session not ready');
        }
        try {
            const response = await api.post('/api/basket/add/', {
                product_id: product.id,
                quantity,
                sku,
                ...metadata
            });
            
            if (response?.data) {
                if (response.data.basket_count !== undefined) {
                    updateBasketCount(response.data.basket_count);
                } else {
                    await fetchBasketCount();
                }
            }

            return response;
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        }
    };

    return (
        <BasketContext.Provider value={{ 
            basketCount, 
            updateBasketCount, 
            isUpdating,
            loading,
            sessionReady,
            fetchBasketCount,
            addToCart
        }}>
            {children}
        </BasketContext.Provider>
    );
};
